import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, adminToken, messageFrom } from "@/lib/api";

const EMPTY_COUNTS = { open: 0, overdue: 0, handled: 0, total: 0 };

/**
 * All of the dashboard's state and every call it makes to the backend.
 *
 * Keeping it here means the page itself is only layout, and it keeps the one
 * rule that matters in a single place: any 401 signs the user out rather than
 * leaving them staring at an empty table.
 */
export function useDashboardData() {
  const navigate = useNavigate();
  const [view, setView] = useState("overview");
  const [tab, setTab] = useState("sessions");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [serviceFilter, setServiceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");
  const [escalationState, setEscalationState] = useState("open");
  const [escalationCounts, setEscalationCounts] = useState(EMPTY_COUNTS);
  const [responseClock, setResponseClock] = useState(null);
  const [savingReference, setSavingReference] = useState(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [compareLead, setCompareLead] = useState(null);
  const [financeEnquiry, setFinanceEnquiry] = useState(null);
  const [savingFinance, setSavingFinance] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [team, setTeam] = useState([]);
  const [savingOwner, setSavingOwner] = useState(null);

  useEffect(() => {
    if (!adminToken.get()) navigate("/admin/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  const handleAuthError = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        adminToken.clear();
        navigate("/admin/login", { replace: true });
        return true;
      }
      return false;
    },
    [navigate],
  );

  const loadStats = useCallback(async () => {
    try {
      const data = await adminApi.stats();
      setStats(data);
      setEscalationCounts((current) => ({
        ...current,
        open: data?.escalations_open ?? current.open,
        overdue: data?.escalations_overdue ?? current.overdue,
      }));
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    }
  }, [handleAuthError]);

  const loadRows = useCallback(async () => {
    if (view !== "overview") return;
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (serviceFilter !== "all") params.service = serviceFilter;
      if (search) params.q = search;
      let data;
      if (tab === "sessions") {
        if (statusFilter !== "all") params.status = statusFilter;
        data = await adminApi.sessions(params);
      } else if (tab === "leads") {
        if (leadStatusFilter !== "all") params.status = leadStatusFilter;
        data = await adminApi.leads(params);
      } else {
        params.state = escalationState;
        data = await adminApi.escalations(params);
        setEscalationCounts(data.counts || EMPTY_COUNTS);
        if (data.sla) setResponseClock(data.sla);
      }
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    } finally {
      setLoading(false);
    }
  }, [
    view,
    tab,
    serviceFilter,
    statusFilter,
    leadStatusFilter,
    escalationState,
    search,
    handleAuthError,
  ]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  /* ---- who owns what -------------------------------------------------- */

  const loadTeam = useCallback(async () => {
    try {
      const data = await adminApi.team();
      setTeam(data?.members || []);
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    }
  }, [handleAuthError]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  /**
   * Add one name to the roster and hand it straight back, so the picker that
   * asked for it can assign the work in the same breath.
   */
  const addTeamMember = async (name) => {
    try {
      const data = await adminApi.addTeamMember(name);
      setTeam(data?.members || []);
      toast.success(`${data?.member?.name || name} added to your team`);
      return data?.member?.name || null;
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
      return null;
    }
  };

  /** Patch one row, and the open finance panel with it, from the saved record. */
  const applyOwner = (reference, updated) => {
    setRows((current) =>
      current.map((row) => (row.reference === reference ? { ...row, ...updated } : row)),
    );
    setFinanceEnquiry((current) =>
      current && current.reference === reference ? { ...current, ...updated } : current,
    );
  };

  const assignOwner = async (reference, owner, kind) => {
    if (!reference) return;
    setSavingOwner(reference);
    try {
      const result =
        kind === "finance"
          ? await adminApi.assignFinanceEnquiryOwner(reference, owner)
          : await adminApi.assignEscalationOwner(reference, owner);
      applyOwner(reference, result?.item || {});
      toast.success(owner ? `Assigned to ${owner}` : "Left unassigned");
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    } finally {
      setSavingOwner(null);
    }
  };

  const assignEscalationOwner = (reference, owner) =>
    assignOwner(reference, owner, "escalation");
  const assignFinanceOwner = (reference, owner) => assignOwner(reference, owner, "finance");

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([loadStats(), loadRows()]);
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const exportCsv = async () => {
    try {
      const params = { type: tab };
      if (serviceFilter !== "all") params.service = serviceFilter;
      if (tab === "sessions" && statusFilter !== "all") params.status = statusFilter;
      if (tab === "leads" && leadStatusFilter !== "all") params.status = leadStatusFilter;
      if (tab === "fallbacks") params.state = escalationState;
      if (search) params.q = search;
      const filename = await adminApi.exportCsv(params);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    }
  };

  const toggleHandled = async (reference, handled) => {
    if (!reference) return;
    setSavingReference(reference);
    try {
      const result = await adminApi.markEscalationHandled(reference, handled);
      const updated = result?.item;
      setRows((current) =>
        escalationState === "all"
          ? current.map((row) => (row.reference === reference ? { ...row, ...updated } : row))
          : current.filter((row) => row.reference !== reference),
      );
      setEscalationCounts((current) => ({
        ...current,
        open: Math.max(0, current.open + (handled ? -1 : 1)),
        overdue: Math.max(0, current.overdue + (handled && updated?.overdue ? -1 : 0)),
        handled: Math.max(0, current.handled + (handled ? 1 : -1)),
        total: current.total,
      }));
      setTotal((current) => (escalationState === "all" ? current : Math.max(0, current - 1)));
      toast.success(handled ? "Marked as handled" : "Moved back to waiting on us");
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    } finally {
      setSavingReference(null);
    }
  };

  /**
   * Move a scoping enquiry along. The row and the open panel are both updated
   * from the backend's reply, so the badge never drifts from what was saved.
   */
  const setFinanceStatus = async (reference, status) => {
    if (!reference || !status) return;
    setSavingFinance(true);
    try {
      const result = await adminApi.setFinanceEnquiryStatus(reference, status);
      const updated = result?.item;
      setRows((current) =>
        current.map((row) =>
          row.record_type === "finance_enquiry" && row.reference === reference
            ? { ...row, ...updated }
            : row,
        ),
      );
      setFinanceEnquiry((current) =>
        current && current.reference === reference ? { ...current, ...updated } : current,
      );
      toast.success(`Marked as ${(updated?.status_label || status).toLowerCase()}`);
    } catch (error) {
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    } finally {
      setSavingFinance(false);
    }
  };

  /**
   * Open one record. A lead row hands over the whole row so the quote is found
   * by its own reference: a customer who quoted twice in one browser session
   * shares a `session_id`, so opening by session showed the wrong quote.
   */
  const openSession = async (target) => {
    const row = typeof target === "string" ? null : target;
    const sessionId = row ? row.session_id : target;
    const quoteRef = row?.quote_ref;
    if (!quoteRef && !sessionId) return;
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      setDetail(
        quoteRef
          ? await adminApi.leadDetail(quoteRef)
          : await adminApi.sessionDetail(sessionId),
      );
    } catch (error) {
      setDetail(null);
      if (!handleAuthError(error)) toast.error(messageFrom(error));
    } finally {
      setDetailLoading(false);
    }
  };

  const signOut = () => {
    adminToken.clear();
    navigate("/admin/login", { replace: true });
  };

  const maxServiceCount = useMemo(() => {
    if (!stats?.by_service?.length) return 1;
    return Math.max(1, ...stats.by_service.map((s) => s.sessions_started));
  }, [stats]);

  return {
    view,
    setView,
    tab,
    setTab,
    stats,
    rows,
    total,
    loading,
    refreshing,
    filters: {
      serviceFilter,
      setServiceFilter,
      statusFilter,
      setStatusFilter,
      leadStatusFilter,
      setLeadStatusFilter,
      escalationState,
      setEscalationState,
      query,
      setQuery,
    },
    escalationCounts,
    responseClock,
    savingReference,
    detail,
    setDetail,
    detailLoading,
    compareLead,
    setCompareLead,
    financeEnquiry,
    setFinanceEnquiry,
    savingFinance,
    setFinanceStatus,
    team,
    savingOwner,
    addTeamMember,
    assignEscalationOwner,
    assignFinanceOwner,
    maxServiceCount,
    handleAuthError,
    refreshAll,
    exportCsv,
    toggleHandled,
    openSession,
    signOut,
  };
}
